import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Command, HelpCircle } from 'lucide-react';
import StartMenu from '@/components/system/StartMenu';
import { NotificationBell } from '@/components/system/NotificationBell';
import { isMacPlatform } from '@/lib/platform';

import { useWindows, type WindowItem } from '@/hooks/useWindows';

import { useWidgetArea } from '@/contexts/WidgetAreaContext';
import { useVoiceAgent } from '@/contexts/VoiceAgentContext';

import { windowAppIcon } from '@/lib/windowAppIcon';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { useDriver } from '@/hooks/useDriver';
import { TASKBAR_HEIGHT } from '@/shared/ui';
import { cn } from '@/lib/utils';
import { useAdaptiveTransition } from '@/hooks/useAdaptiveTransition';
import { TaskbarPreview } from '@/components/system/TaskbarPreview';
import { TaskbarRecordingIndicator } from '@/components/system/TaskbarRecordingIndicator';
import { taskbarButtonRegistry } from '@/services/taskbar-button-registry';

function formatTime(date: Date, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Berlin',
  }).format(date);
}

// Gruppiere Fenster nach appId
type GroupedWindows = {
  appId: string;
  instances: WindowItem[];
  hasActiveInstance: boolean;
}

function groupWindowsByAppId(windows: WindowItem[], activeId: string | null): GroupedWindows[] {
  const groups = new Map<string, WindowItem[]>();

  windows.forEach((w) => {
    const existing = groups.get(w.appId) || [];
    existing.push(w);
    groups.set(w.appId, existing);
  });

  return Array.from(groups.entries()).map(([appId, instances]) => {
    const hasActiveInstance = instances.some((w) => w.id === activeId && !w.minimized);

    return {
      appId,
      instances,
      hasActiveInstance,
    };
  });
}

export default function Taskbar() {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const [time, setTime] = useState<string>(() => formatTime(new Date(), locale));
  const { windows, activeId, toggleMinimize, focusWindow, closeWindow } = useWindows();
  const { settings } = useSettingsContext();
  const { displayMode } = settings.taskbar;
  const { toggle: toggleWidgetArea } = useWidgetArea();
  const { state: voiceState } = useVoiceAgent();
  const { getTiming } = useAdaptiveTransition();
  const [hoveredPreview, setHoveredPreview] = useState<{ appId: string; buttonRect: DOMRect } | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { startTour, stopTour, activeTourId, isRunning } = useDriver();
  const desktopTourId = 'desktop-intro';
  const isDesktopTourActive = activeTourId === desktopTourId && isRunning;
  const desktopTourLabel = t(isDesktopTourActive ? 'taskbar.stopTour' : 'taskbar.openTour');

  const handleDesktopTourClick = () => {
    if (isDesktopTourActive) {
      stopTour();
      return;
    }
    startTour(desktopTourId, { ensureElements: true });
  };

  const taskbarTiming = useMemo(
    () => getTiming('quick', { context: 'taskbar', clamp: { min: 100, max: 220 } }),
    [getTiming],
  );

  // Gruppiere Fenster nach appId
  const groupedWindows = useMemo(() => {
    return groupWindowsByAppId(windows, activeId);
  }, [windows, activeId]);

  useEffect(() => {
    const tick = () => setTime(formatTime(new Date(), locale));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [locale]);

  const handleGroupClick = (group: GroupedWindows) => {
    const { instances } = group;

    if (instances.length === 1) {
      // Nur eine Instanz: Normales Toggle-Verhalten (Focus/Minimize)
      const w = instances[0];
      if (w.minimized) {
        focusWindow(w.id);
      } else if (activeId === w.id) {
        toggleMinimize(w.id);
      } else {
        focusWindow(w.id);
      }
    } else {
      // Mehrere Instanzen: Nur fokussieren, kein Toggle
      // Minimieren/Maximieren erfolgt über die Hover-Preview
      const activeInstance = instances.find((w) => w.id === activeId && !w.minimized);
      const firstNonMinimized = instances.find((w) => !w.minimized);
      const target = activeInstance || firstNonMinimized || instances[0];

      // Immer nur fokussieren, nie minimieren bei mehreren Instanzen
      focusWindow(target.id);
    }
  };

  const handleMouseEnter = (appId: string, buttonElement: HTMLElement) => {
    // Lösche vorheriges Timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Zeige Vorschau nach 500ms
    hoverTimeoutRef.current = setTimeout(() => {
      const rect = buttonElement.getBoundingClientRect();
      setHoveredPreview({ appId, buttonRect: rect });
    }, 500);
  };

  const handleMouseLeave = () => {
    // Lösche Timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Verzögere das Schließen der Vorschau, damit man zur Vorschau hovern kann
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredPreview(null);
    }, 300);
  };

  const handlePreviewMouseEnter = () => {
    // Verhindere, dass die Vorschau verschwindet wenn man darüber hovert
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePreviewMouseLeave = () => {
    // Schließe Vorschau sofort wenn man sie verlässt
    setHoveredPreview(null);
  };

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="taskbar-shell z-[10000] flex w-full items-center justify-between px-4"
      style={{
        height: TASKBAR_HEIGHT,
        ['--taskbar-motion-duration' as string]: `${taskbarTiming.duration}ms`,
      }}
      role="contentinfo"
      aria-label={t('taskbar.aria')}
      data-display-mode={displayMode}
    >
      <div className="taskbar-left flex items-center h-full">
        <StartMenu tileClassName="bg-transparent" />
      </div>
      <div
        className="taskbar-dock flex-1 h-full flex items-center justify-center gap-3 px-3 overflow-x-auto"
        data-driver-id="taskbar-dock"
      >
        <AnimatePresence mode="popLayout">
          {groupedWindows.map((group) => {
            const { appId, instances, hasActiveInstance } = group;
            const Icon = windowAppIcon(appId);
            const showIcon = (displayMode === 'both' || displayMode === 'icon') && !!Icon;
            const showLabel = displayMode === 'both' || displayMode === 'name';
            const appTitle = instances[0]?.title.replace(/\s*\(\d+\)$/, '') || appId;

            return (
              <motion.div
                key={appId}
                className="relative"
                layout
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                transition={{
                  layout: {
                    type: 'spring',
                    stiffness: 400,
                    damping: 30,
                  },
                  opacity: { duration: 0.15 },
                  scale: { duration: 0.15 },
                  y: { duration: 0.15 },
                }}
              >
                <button
                  ref={(el) => taskbarButtonRegistry.register(appId, el)}
                  className={cn(
                    'taskbar-button',
                    showLabel && 'taskbar-button--with-label',
                    !showLabel && 'taskbar-button--icon-only',
                    instances.length > 1 && 'taskbar-button--with-badge',
                    hasActiveInstance ? 'taskbar-button--active' : 'taskbar-button--inactive'
                  )}
                  data-active={hasActiveInstance ? 'true' : 'false'}
                  onClick={() => handleGroupClick(group)}
                  onMouseEnter={(e) => handleMouseEnter(appId, e.currentTarget)}
                  onMouseLeave={handleMouseLeave}
                  aria-label={appTitle}
                  aria-pressed={hasActiveInstance}
                >
                  <span className="taskbar-button__content">
                    {showIcon && Icon && (
                      <span className="taskbar-button__icon" aria-hidden>
                        <Icon className="taskbar-button__icon-svg" />
                      </span>
                    )}
                    {showLabel && (
                      <span className="taskbar-button__label">
                        {appTitle}
                      </span>
                    )}
                  </span>
                  {instances.length > 1 && (
                    <span className="taskbar-button__badge" aria-hidden="true">
                      {instances.length}
                    </span>
                  )}
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
      <div className="taskbar-right flex items-center h-full gap-3">
        {/* Global recording status (cdb_webos#393) */}
        <TaskbarRecordingIndicator />


        {/* Voice Agent Session Indicator - Wellenförmige Animation */}
        <AnimatePresence>
          {voiceState.isConnected && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="h-full flex items-center justify-center"
            >
              <button
                data-widget-bar-toggle
                onClick={toggleWidgetArea}
                className="p-2 hover:bg-white/10 rounded transition-colors"
                aria-label={t('taskbar.voiceAgentActive')}
                title={t('taskbar.voiceAgentSession')}
              >
                <div className="flex items-center gap-0.5 h-5">
                  {[0, 1, 2, 3, 4].map((index) => (
                    <motion.div
                      key={index}
                      className="w-1 bg-green-500 rounded-full"
                      animate={{
                        height: ['40%', '100%', '40%'],
                      }}
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: index * 0.15,
                      }}
                    />
                  ))}
                </div>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Command Palette (cdb_webos#204) — also hosts Feature Request/Bug
            Report/Changelog now, see CommandPalette.tsx's utility row */}
        <button
          type="button"
          className="h-full flex items-center justify-center cursor-pointer hover:bg-white/10 px-3 rounded transition-colors"
          onClick={() => window.dispatchEvent(new CustomEvent('open-command-palette'))}
          aria-label={t('commandPalette.taskbarLabel', { shortcut: isMacPlatform() ? '⌘K' : 'Ctrl K' })}
          title={t('commandPalette.taskbarLabel', { shortcut: isMacPlatform() ? '⌘K' : 'Ctrl K' })}
          data-driver-id="taskbar-command-palette"
        >
          <Command className="h-5 w-5 transition-colors" />
        </button>

        {/* Notification Bell */}
        <span data-driver-id="taskbar-notification-bell">
          <NotificationBell className="h-full flex items-center justify-center" />
        </span>

        <button
          type="button"
          className={cn(
            'taskbar-help h-full flex items-center justify-center cursor-pointer hover:bg-white/10 px-3 rounded transition-colors',
            isDesktopTourActive && 'text-primary'
          )}
          onClick={handleDesktopTourClick}
          aria-label={desktopTourLabel}
          title={desktopTourLabel}
          aria-pressed={isDesktopTourActive}
          data-driver-id="desktop-tour-button"
        >
          <HelpCircle
            className={cn(
              'h-5 w-5 transition-colors',
              isDesktopTourActive && 'text-primary'
            )}
          />
        </button>

        {/* Clock - Opens Widget Area */}
        <button
          data-widget-bar-toggle
          className="taskbar-clock h-full flex items-center justify-center cursor-pointer hover:bg-white/10 px-3 rounded transition-colors"
          onClick={toggleWidgetArea}
          aria-label={t('taskbar.toggleWidgetArea')}
          data-driver-id="taskbar-clock"
        >
          <time
            className="taskbar-clock__time select-none tabular-nums"
            aria-label={t('taskbar.timeBerlin')}
          >
            {time}
          </time>
        </button>
      </div>

      {/* Hover-Vorschau */}
      <AnimatePresence>
        {hoveredPreview && (() => {
          const group = groupedWindows.find((g) => g.appId === hoveredPreview.appId);
          if (!group) return null;
          const fullTitle = group.instances[0]?.title.replace(/\s*\(\d+\)$/, '') || group.appId;

          return (
              <TaskbarPreview
                appId={group.appId}
                appTitle={fullTitle}
                instances={group.instances}
                Icon={windowAppIcon(group.appId)}
                activeId={activeId}
                onFocus={focusWindow}
                onClose={closeWindow}
                onMinimize={toggleMinimize}
                buttonRect={hoveredPreview.buttonRect}
                onHoverEnter={handlePreviewMouseEnter}
                onHoverLeave={handlePreviewMouseLeave}
              />
          );
        })()}
      </AnimatePresence>
    </div>
  );
}
